/**
 * Verkleinert ein Foto im Browser, bevor es hochgeladen wird.
 *
 * Kamerafotos scheitern heute an der Größengrenze des Servers, **bevor** irgendetwas passiert —
 * die serverseitigen Varianten entstehen erst danach. Verkleinert wird deshalb hier, vor dem
 * Absenden.
 *
 * **2560 px lange Kante** ist keine gefühlte Zahl: der Lightbox-Betrachter zeigt maximal 1280 px
 * CSS-Breite, auf einem Retina-Bildschirm sind das 2560 echte Bildpunkte. Mehr sieht niemand.
 *
 * **JPEG bleibt JPEG, kein WebP.** Unsere Fotos gehen ohnehin nicht an die Portale; die ~25 %
 * Bytes, die WebP spart, wiegen eine neue Fehlerklasse nicht auf.
 *
 * **Nicht für Beweisfotos.** Schadens- und Reinigungsaufnahmen behalten Original und EXIF — dort
 * zählt, was die Kamera aufgezeichnet hat, nicht wie klein die Datei ist.
 */
export const PORTAL_PHOTO_MAX_EDGE = 2560;
const JPEG_QUALITY = 0.85;
/**
 * Größer wird im Browser nicht dekodiert: ein Bild dieser Größe kann am Handy den Tab sprengen,
 * bevor es kleiner ist. Die Datei geht dann unverändert an die Größenprüfung des Aufrufers.
 */
export const MAX_DECODE_BYTES = 40 * 1024 * 1024;

export async function downscalePortalPhoto(
  file: File,
  maxEdge: number = PORTAL_PHOTO_MAX_EDGE,
): Promise<File> {
  if (!isDownscalableImage(file) || file.size > MAX_DECODE_BYTES) return file;

  try {
    // `imageOrientation: 'from-image'` ist Pflicht: Canvas verwirft die EXIF-Ausrichtung, und
    // ohne diesen Hinweis liegt jedes Hochformat-Foto anschliessend quer.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const longest = Math.max(bitmap.width, bitmap.height);
    const scale = longest > maxEdge ? maxEdge / longest : 1;
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      bitmap.close();
      warnNotDownscaled('no-2d-context');
      return file;
    }
    // Weiss darunter: ein PNG mit Alphakanal wuerde als JPEG sonst schwarz statt transparent.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const blob = await toJpegBlob(canvas);
    if (!blob) {
      warnNotDownscaled('no-blob');
      return file;
    }
    // Ein bereits sparsam codiertes Foto kann durch das Neucodieren groesser werden. Dann ist das
    // Original die bessere Datei — verkleinert wird, um Bytes zu sparen, nicht um ihrer selbst willen.
    if (blob.size >= file.size) return file;

    return new File([blob], toJpegName(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch (error) {
    // Ein Browser ohne `createImageBitmap`-Optionen oder eine kaputte Datei darf den Upload nicht
    // verhindern. Dann geht das Original raus — schlimmstenfalls wie bisher.
    warnNotDownscaled(error instanceof Error ? error.name : 'unknown');
    return file;
  }
}

/** Nur der Grund, nie der Dateiname: er kann Namen von Gästen oder Adressen tragen. */
function warnNotDownscaled(reason: string) {
  console.warn('[upload] photo not downscaled', { reason });
}

/** HEIC und SVG bleiben aussen vor: das eine kann Canvas nicht lesen, das andere ist kein Foto. */
function isDownscalableImage(file: File): boolean {
  return file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp';
}

function toJpegBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
}

function toJpegName(name: string): string {
  const withoutExtension = name.replace(/\.[^.]+$/, '');
  return `${withoutExtension || 'foto'}.jpg`;
}
